import { BffError } from './bffError';

/**
 * Diccionario único de errores del BFF de auto/moto: `error.code` -> mensaje en
 * español mostrable al usuario. El `message` que manda el BFF no se muestra
 * nunca (puede traer texto crudo del proveedor).
 */

/**
 * La emisión quedó colgada y la confirma una persona (§3.5,
 * `requiereAsistencia`). No es un error: la solicitud salió, y reintentar
 * arriesga un segundo cobro.
 */
export const MENSAJE_EMISION_EN_REVISION =
  'Estamos confirmando tu póliza con la aseguradora. Te contactamos apenas esté lista.';

export const MENSAJE_GENERICO =
  'Se produjo un error inesperado. Intentá nuevamente en unos minutos.';

/** 422 `embudo_rechazado` sin mensaje de Salesforce (US 23704). */
export const MENSAJE_RECHAZO_POR_DEFECTO = 'Revisá los datos ingresados.';

/** code -> mensaje. */
export const MENSAJES_ERROR: Record<string, string> = {
  validation_failed: 'Revisá los datos: hay campos incompletos o con errores.',
  recaptcha_invalid:
    'No pudimos validar que no seas un robot. Recargá la página e intentá de nuevo.',
  blocked: 'No podemos continuar con esta cotización. Comunicate con nosotros.',
  too_many_requests:
    'Hiciste muchas cotizaciones seguidas. Esperá unos minutos y volvé a intentar.',
  recaptcha_unavailable:
    'No pudimos validar la seguridad de la página. Intentá de nuevo en unos minutos.',
  plan_not_selected:
    'Elegí un plan antes de continuar con la contratación.',
  origen_not_found:
    'No pudimos identificar el origen de la cotización. Volvé a empezar desde la página principal.',
  quote_token_expired:
    'Tu cotización venció. Volvé a cargar tus datos para ver precios actualizados.',
  quote_token_invalid:
    'Tu cotización ya no es válida. Volvé a cargar tus datos para cotizar de nuevo.',
  quotation_not_found:
    'No encontramos tu cotización. Volvé a cotizar para continuar.',
  emission_disabled:
    'En este momento no podemos emitir online. Un asesor te contacta para terminar la contratación.',
  issue_in_progress:
    'Ya estamos procesando tu emisión. Esperá unos segundos, no la envíes de nuevo.',
  issue_rejected: 'La aseguradora no pudo aceptar la solicitud.',
  issue_missing_data:
    'Faltan datos para emitir la póliza. Revisá los datos personales y del vehículo.',
  provider_error:
    'La aseguradora no está respondiendo. Intentá nuevamente en unos minutos.',
  provider_timeout:
    'Estamos confirmando tu emisión con la aseguradora. No cierres esta página.',
  document_not_ready:
    'El documento todavía no está disponible. Volvé a intentar en unos minutos.',
  document_not_available:
    'La aseguradora no emite este documento para tu póliza.',
  payment_method_not_supported:
    'Por ahora solo podemos emitir con tarjeta de crédito.',
  payment_data_not_allowed:
    'No podemos guardar datos de pago en este paso. Continuá hasta el paso de pago.',
  not_applicable: 'Esta opción no está disponible para este vehículo.',
  // Solo con el token del canje de /retomar (seleccion o emision con precios vencidos): el funnel
  // normal nunca recibe este code.
  precios_vencidos:
    'Los precios vencieron. Volvé a abrir el link del mail para actualizarlos.',
  network_error:
    'No pudimos conectarnos. Verificá tu conexión e intentá nuevamente.',
  unexpected_error: MENSAJE_GENERICO,
};

/**
 * `details.reason` de un 422 `issue_rejected` -> mensaje. El BFF clasifica el
 * error del proveedor en estas cuatro razones (contrato §3.5).
 */
export const MENSAJES_RECHAZO_EMISION: Record<string, string> = {
  missing_data:
    'Faltan datos para emitir la póliza. Revisá los datos personales y del vehículo.',
  invalid_data:
    'Alguno de los datos no es válido para la aseguradora. Revisalos y volvé a intentar.',
  underwriting_rejected:
    'La aseguradora no aprobó la cobertura para este vehículo. Un asesor te contacta con alternativas.',
  provider_unavailable:
    'La aseguradora no está disponible en este momento. Intentá nuevamente en unos minutos.',
};

/**
 * Mensajes por campo para un 400 `validation_failed`. La clave es el campo del
 * DTO (notación con punto, tal cual lo manda el BFF en `details`).
 */
export const MENSAJES_CAMPO: Record<string, string> = {
  'persona.email': 'Ingresá una dirección de correo electrónico válida.',
  'persona.nombre': 'Ingresá tu nombre.',
  'persona.apellido': 'Ingresá tu apellido.',
  'persona.telefono': 'Ingresá un número de teléfono válido.',
  'persona.codigoArea': 'Ingresá un código de área válido.',
  'persona.fechaNacimiento': 'Ingresá una fecha de nacimiento válida.',
  'persona.provinciaId': 'Elegí una provincia.',
  'persona.localidad': 'Ingresá tu localidad.',
  'persona.codigoPostal': 'Ingresá un código postal de 4 dígitos.',
  'vehiculo.patente': 'Ingresá una patente válida.',
  'vehiculo.anio': 'Elegí un año válido.',
  'tomador.numeroDocumento': 'Ingresá un número de documento válido.',
  'tomador.cuil': 'Ingresá un CUIL válido.',
  'tomador.domicilio.codigoPostal': 'Ingresá un código postal de 4 dígitos.',
  'pago.tarjeta.numero': 'Ingresá un número de tarjeta válido.',
  'pago.cbu.numero': 'Ingresá un CBU válido.',
};

/** Mensaje para un campo puntual de `details`; genérico si no está mapeado. */
export const mensajeDeCampo = (campo: string): string =>
  MENSAJES_CAMPO[campo] || 'Revisá este dato.';

/**
 * Mensaje mostrable para un error del BFF. Un `issue_rejected` se afina por
 * `details.reason`; un `validation_failed` con un solo campo muestra el mensaje
 * de ese campo.
 */
export const mensajeDeError = (error: BffError): string => {
  // Embudo EOL (US 23704): el mensaje lo escribe Salesforce para el cliente y
  // dice que dato corregir. Es la unica excepcion a "nunca mostrar `message`".
  if (error.code === 'embudo_rechazado') {
    return (error.backendMessage || '').trim() || MENSAJE_RECHAZO_POR_DEFECTO;
  }
  if (error.code === 'issue_rejected') {
    const reason = error.reason;
    if (reason && MENSAJES_RECHAZO_EMISION[reason]) {
      return MENSAJES_RECHAZO_EMISION[reason];
    }
    return MENSAJES_ERROR.issue_rejected;
  }
  // 422 de la compañía por un dato que exige (Bug 24086 / US 24094): `details.field` lo nombra.
  if (error.code === 'issue_missing_data') {
    const campo = error.details?.field;
    return typeof campo === 'string' && campo.trim()
      ? `Falta un dato para emitir: ${campo.trim()}.`
      : MENSAJES_ERROR.issue_missing_data;
  }
  if (error.code === 'validation_failed') {
    const campos = Object.keys(error.camposInvalidos);
    if (campos.length === 1) return mensajeDeCampo(campos[0]);
  }
  return MENSAJES_ERROR[error.code] || MENSAJE_GENERICO;
};

/** `campo -> mensaje` para pintar los errores al lado de cada input. */
export const mensajesPorCampo = (error: BffError): Record<string, string> => {
  const salida: Record<string, string> = {};
  Object.keys(error.camposInvalidos).forEach((campo) => {
    salida[campo] = mensajeDeCampo(campo);
  });
  return salida;
};

/** El token de cotización venció o no sirve: hay que volver al paso de datos. */
export const requiereVolverADatos = (error: BffError): boolean =>
  error.code === 'quote_token_expired' ||
  error.code === 'quote_token_invalid' ||
  error.code === 'quotation_not_found';
